import { FormField, getError } from 'atoms/create-resource-atom';
import { FieldState, getError as newGetError } from 'atoms/form-atoms';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { ComboBox, ComboBoxProps, Layer, Loading } from '@carbon/react';

import { useAtom, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { ReferenceLocationResponse } from 'types/api-responses';
import styles from './survey-area-autocomplete.module.scss';

interface FormProps extends Omit<
  ComboBoxProps<ReferenceLocationResponse>,
  'items'
> {
  location?:
    | FormField<ReferenceLocationResponse | undefined>
    | FieldState<ReferenceLocationResponse | undefined>;
  onChange: (data: {
    selectedItem: ReferenceLocationResponse | undefined | null;
  }) => void;
  index?: number;
  additionalPayload?: ReferenceLocationSearchParams;
  light?: boolean;
  disabled?: boolean;
  className?: string;
  /** When true, only shows invalid state after field has been touched */
  validateOnlyWhenDirty?: boolean;
  /**
   * Minimum characters before a search request goes out.
   *
   * 0 (the default) keeps the original behaviour, including the empty-text prime
   * on first focus, so the menu opens showing the first page. Any value > 0 makes
   * the field strictly typing-driven: no prime, and no request until that many
   * characters are typed. The Unit Builder asks for 2; every other consumer takes
   * the default and is unchanged.
   */
  searchMinChars?: number;
}

interface ReferenceLocationSearchParams extends SearchParams {
  adjacentQuarteredGeographies?: Array<{
    referenceLocationId: number;
    quarterCalls: string[];
  }>;
  /** When true, only returns reference locations with a matching Salesforce LandGridTract */
  requireSalesforceLandGridTract?: boolean;
}

const SurveyAreaAutocomplete = ({
  location,
  onChange,
  additionalPayload,
  className,
  light = false,
  disabled = false,
  titleText = 'Survey Areas / Applicable Areas',
  validateOnlyWhenDirty = false,
  searchMinChars = 0,
  onFocus,
  helperText,
  ...rest
}: FormProps) => {
  const { searchAtom, resetSearchDataAtom } = useMemo(
    () =>
      searchAtomPair<ReferenceLocationResponse, ReferenceLocationSearchParams>(
        'referenceLocations'
      ),
    []
  );

  const [query, setPayload] = useAtom(searchAtom);
  const resetSearchData = useSetAtom(resetSearchDataAtom);

  const debouncedSetPayload = useMemo(
    () => debounce(setPayload, 400),
    [setPayload]
  );
  // Drop a pending search on unmount: each row owns its own atom pair and is torn
  // down with the panel, so a late keystroke would otherwise write to a dead one.
  useEffect(() => () => debouncedSetPayload.cancel(), [debouncedSetPayload]);

  /**
   * The search results, with the current selection folded in.
   *
   * Carbon reconciles the field when the menu closes by looking for the input's
   * text in `items` (`ComboBox.js`, the `stateReducer`), and clears the selection
   * when it finds nothing. `items` here is the live search, which resets as soon
   * as the field is focused — so the selected tract was usually absent, and
   * clicking away wiped a row the operator had already filled. Keeping the
   * selection in the list gives that lookup something to match.
   */
  const items = useMemo(() => {
    const results = query?.data?.results || [];
    const selected = location?.value;
    if (!selected) return results;
    return results.some((result) => result.id === selected.id)
      ? results
      : [selected, ...results];
  }, [query?.data?.results, location?.value]);

  /**
   * Runs the search once with no text, so opening the field shows something.
   *
   * The list only ever came from `onInputChange`, so the menu opened empty and
   * the operator had to guess a few characters before anything appeared. Primed
   * on first focus rather than on mount because each instance owns its own search
   * atoms (`searchAtomPair` builds a fresh pair per component), so a mount-time
   * call would fire one request per row for a list nobody may open.
   *
   * Guarded on `query?.data`: only the first focus asks, and typing afterwards
   * replaces the results as it always did.
   *
   * Skipped entirely when a minimum search length is set: priming IS a search
   * with zero characters, so a typing-driven field has nothing to prime.
   */
  const primeSearch = () => {
    if (searchMinChars > 0) return;
    if (query?.data) return;
    setPayload((current) => ({
      ...current,
      text: '',
      pageSize: 10,
      ...additionalPayload,
    }));
  };

  /**
   * Whether this field's own search is in flight.
   *
   * Per instance, not per page: `searchAtomPair` above builds a fresh atom pair
   * for each mount, so a search started by one survey row cannot light up the
   * spinner on another.
   */
  const loading = !!query?.loading;

  const spinner = (
    <Loading small withOverlay={false} description="Loading survey areas" />
  );

  const comboBox = (
    <ComboBox<ReferenceLocationResponse>
      titleText={
        titleText ? (
          <span className={styles.title}>
            {titleText}
            {loading && spinner}
          </span>
        ) : (
          titleText
        )
      }
      items={items}
      className={className}
      itemToString={(data) => {
        return data?.name || '';
      }}
      itemToElement={(result) => <>{result.name}</>}
      selectedItem={location?.value || null}
      onChange={({ selectedItem }) => {
        onChange?.({ selectedItem: selectedItem ?? undefined });
      }}
      onInputChange={(text) => {
        resetSearchData(undefined);
        if ((text ?? '').trim().length < searchMinChars) {
          // Below the threshold: drop any burst already queued, or it would fire
          // with the previous, longer text after the user deletes back down.
          debouncedSetPayload.cancel();
          return;
        }
        debouncedSetPayload((c) => ({
          ...c,
          text,
          pageSize: 10,
          ...additionalPayload,
        }));
      }}
      // Say why the menu is empty, rather than letting Carbon report "no results"
      // for a search that never ran. A consumer's own `helperText` still wins
      // whenever this does not apply.
      helperText={
        searchMinChars > 0 && !loading && items.length === 0
          ? `Type at least ${searchMinChars} characters to search`
          : helperText
      }
      invalid={
        location
          ? 'isValid' in location
            ? validateOnlyWhenDirty
              ? !location.isValid && location.isDirty
              : !location.isValid
            : !!getError(location)
          : false
      }
      invalidText={
        location
          ? 'isValid' in location
            ? validateOnlyWhenDirty && !location.isDirty
              ? ''
              : newGetError(location)
            : getError(location)
          : ''
      }
      disabled={disabled}
      {...rest}
      // After `rest`, so the prime cannot be dropped by a consumer's own handler
      // — theirs still runs.
      onFocus={(event) => {
        primeSearch();
        onFocus?.(event);
      }}
    />
  );

  // An unlabelled field keeps the spinner beside the input instead; see the
  // stylesheet for why the slot is always present.
  const field = titleText ? (
    comboBox
  ) : (
    <div className={styles.fieldWithSpinner}>
      {comboBox}
      <span className={styles.spinnerSlot}>{loading && spinner}</span>
    </div>
  );

  return light ? <Layer>{field}</Layer> : field;
};

export { SurveyAreaAutocomplete };
